import React from "react";
import { Code, Table, Tbody, TD, Thead } from "../../../../pages/KnowledgeCenterPage/ApiDocs/primitives";
import { AdminGuideSectionId, GuideLink, Lead } from "../primitives";

interface Problem {
  symptom: React.ReactNode;
  cause: React.ReactNode;
  fix: React.ReactNode;
  see: AdminGuideSectionId;
  seeLabel: string;
}

const PROBLEMS: Problem[] = [
  {
    symptom: <>403 saying the user "has access to 0/N objects"</>,
    cause: "The role reaches the API, but its level on the endpoints group is below the method's level.",
    fix: "Check method access on the permission object and the role's level on the endpoints group. Access Explorer shows both: a ✕ marks the method the role's level doesn't meet.",
    see: "method-access",
    seeLabel: "Method access",
  },
  {
    symptom: "403 on a route with an id in it",
    cause: <>The object stores <Code>{"{id}"}</Code> or a number in its path. Requests are matched with ids removed.</>,
    fix: "In Permission Objects, select the object, click Edit, then click Use under the Path field.",
    see: "method-access",
    seeLabel: "Method access",
  },
  {
    symptom: "A user can't see an asset",
    cause: "None of their roles holds an asset group that contains it.",
    fix: "Check Access Explorer, Asset groups (click a group to list its assets), then the Asset Group tab.",
    see: "onboarding-asset-group",
    seeLabel: "Onboarding an asset group",
  },
  {
    symptom: "New asset or resources missing after onboarding",
    cause: "New grants are not live until a refresh runs.",
    fix: "Click Refresh entitlements on the Entitlements tab. It can take up to 15 minutes. For one person now, click Refresh access on them in User Setup.",
    see: "onboarding-an-asset",
    seeLabel: "Onboarding an asset",
  },
  {
    symptom: "New asset or resource not showing on AMP pages",
    cause: "The hourly asset refresh hasn't run since the change.",
    fix: "Click Refresh assets at the top right of the Assets tab. It can take up to 15 minutes.",
    see: "when-changes-apply",
    seeLabel: "When changes apply",
  },
  {
    symptom: "Logic App upload fails for a new asset",
    cause: "The all-folders objects are missing or unmapped.",
    fix: "Click Fix all or Grant folders on the Onboarding tab.",
    see: "filecenter-folders",
    seeLabel: "FileCenter folders",
  },
  {
    symptom: "A client sees File Center but not an asset's folders",
    cause: "The client's FileCenter group doesn't hold that asset's folder objects.",
    fix: "Add them to the group on the Endpoints Group tab.",
    see: "filecenter-folders",
    seeLabel: "FileCenter folders",
  },
  {
    symptom: <>User Setup says <Code>&lt;domain&gt; not in Domain list</Code> (403)</>,
    cause: "The email domain isn't on any company's Client Domain list, or it was added less than 3 hours ago.",
    fix: "Add the domain to the company, then try again later if it was just added.",
    see: "onboarding-company",
    seeLabel: "Onboarding a company",
  },
  {
    symptom: "User Setup Create fails with a 502",
    cause: "No Entra object id came back, often an expired Entra credential. Nothing was saved.",
    fix: "Retry once the credential is fixed. Retrying is safe.",
    see: "onboarding-user",
    seeLabel: "Onboarding a user",
  },
  {
    symptom: "A new role can't be saved",
    cause: "The Role tab needs at least one user.",
    fix: "Invite the users first, then create the role with them.",
    see: "onboarding-client",
    seeLabel: "Onboarding a client",
  },
  {
    symptom: "A client user signs in but has no Landing, Lineview or File Center",
    cause: <>The role lacks <Code>amp_external_client_base</Code>, or the new grants aren't live yet.</>,
    fix: "Check the role's Pages tab in Access Explorer. Then click Refresh entitlements, or Refresh access on the user.",
    see: "onboarding-client",
    seeLabel: "Onboarding a client",
  },
  {
    symptom: "A disabled user still shows roles",
    cause: "This is expected. A disabled user keeps their roles but gets no pages, APIs or asset groups.",
    fix: "Nothing to fix. Remove the roles too if you want them gone.",
    see: "offboarding",
    seeLabel: "Offboarding",
  },
  {
    symptom: "A change didn't apply",
    cause: "Adds and changes wait for a refresh. Only removals apply at once.",
    fix: "Click Refresh entitlements on the Entitlements tab and allow up to 15 minutes. For one person now, click Refresh access on them.",
    see: "when-changes-apply",
    seeLabel: "When changes apply",
  },
];

export const Troubleshooting: React.FC = () => (
  <div className="space-y-6">
    <Lead>Use this when something doesn't work as expected. Find the symptom, then follow the link for details.</Lead>
    <Table minWidth={720}>
      <Thead cols={["Symptom", "Likely cause", "Fix"]} />
      <Tbody>
        {PROBLEMS.map((p, i) => (
          <tr key={i}>
            <td className={`${TD} w-1/4 font-medium`}>{p.symptom}</td>
            <td className={`${TD} w-1/3`}>{p.cause}</td>
            <td className={TD}>
              {p.fix}{" "}
              <span className="whitespace-nowrap">
                See <GuideLink to={p.see}>{p.seeLabel}</GuideLink>.
              </span>
            </td>
          </tr>
        ))}
      </Tbody>
    </Table>
  </div>
);
